import { IAffairPub, IAffairRef } from "@api/affair.int";
import { IUserPub } from "@api/auth.int";
import { IGeoGroupPub } from "@api/geo-group.int";
import { Box, BoxProps, HStack, Icon, IconButton, Text } from "@chakra-ui/react";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FaCity, FaUserSlash } from "react-icons/fa";
import { MdArrowBack, MdArrowForward, MdVisibilityOff } from "react-icons/md";
import { useShallow } from "zustand/react/shallow";
import { AFFAIR_KINDS, Bogyo, BogyoGroup, BogyoGuard } from "./appComponents";
import { DeepErrorBox, DeepErrorContainer } from "./misc";
import { isLocationAffair, useVVStore } from "./store";
import { removeHungarianAccents } from "./utils";
import { useVVNav } from "./vvNav";




export interface IPaginatorProps<TPage, TItem, TNextKeys> {
    parse: (page: TPage) => {
        items: TItem[],
        nextKeys?: TNextKeys
    }
    loadNext: (fromKeys?: TNextKeys) => Promise<TPage>,
    initial?: TItem[],
    deps: React.DependencyList;
}


export function usePaginator<TPage, TItem, TNextKeys>({ parse, loadNext, deps }: IPaginatorProps<TPage, TItem, TNextKeys>) {
    const __pages = useRef<TPage[]>([]);
    const [pageIx, __setPageIx] = useState(-1);
    const [page, setPage] = useState<TPage | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');


    const setPageIx = (v: number) => {
        __setPageIx(v);
        setPage(__pages.current[v]);
    };

    const callLoader = async (fromKeys?: TNextKeys) => {
        try {
            setIsLoading(true);
            setError('');
            const newPage = await loadNext(fromKeys);
            __pages.current.push(newPage);
            setPageIx(__pages.current.length - 1);
        } catch (ex) {
            setError('' + ex);
        } finally {
            setIsLoading(false);
        }

    }

    useEffect(() => {
        __setPageIx(-1);
        __pages.current.length = 0;
        callLoader();
    }, deps);

    const _nextKeys = (page && parse(page).nextKeys && (JSON.stringify(parse(page).nextKeys) != '"{}"')) ? parse(page).nextKeys : undefined;

    const isNext = (pageIx < __pages.current.length - 1) || !!_nextKeys;
    const onNext = !isNext ? undefined : (async () => {
        if (pageIx < __pages.current.length - 1) {
            setPageIx(pageIx + 1);
        } else {
            await callLoader(_nextKeys);
        }
    });

    const onPrev = pageIx === 0 ? undefined : (() => setPageIx(pageIx - 1));

    const updatePage = async (page: TPage) => {
        if (parse(page).items.length == 0) {
            let nextKeys : TNextKeys|undefined = undefined;
            if (pageIx === __pages.current.length-1) {
                nextKeys = parse(page).nextKeys;
            }
            __pages.current.splice(pageIx, 1);
            const ix = Math.min(pageIx, __pages.current.length - 1);
            setPageIx(ix);
            setPage(__pages.current[ix]);
            if (nextKeys) {
                await callLoader(nextKeys);
            }
        } else {
            __pages.current[pageIx] = page;
            setPage(page);
        }
    }

    return {
        page,
        pageNo: pageIx + 1,
        onPrev,
        onNext,
        isLoading, setIsLoading,
        error, setError,
        items: page ? parse(page).items : [],
        updatePage,
    }

}

export type TPaginator<TPage, TItem, TNextKeys> = ReturnType<typeof usePaginator<TPage, TItem, TNextKeys>>;

export interface PaginatorBoxProps<TPage, TItem, TNextKeys> {
    pager: TPaginator<TPage, TItem, TNextKeys>,
    item: (item: TItem) => React.ReactNode,
}


export function PaginatorBox<TPage, TItem, TNextKeys>({ pager, item }: PaginatorBoxProps<TPage, TItem, TNextKeys>) {
    const resultCount = pager.items.length;
    return <>
        {!pager.page ? <DeepErrorBox isLoading={pager.isLoading} error={pager.error}  ></DeepErrorBox>
            : <DeepErrorContainer isLoading={pager.isLoading} error={pager.error} onErrorClose={() => pager.setError('')} >
                <Box flex='1' overflowY='auto' overflowX='hidden'>
                    {pager.items.map(i => item(i))}
                </Box>
                {((pager.onPrev || pager.onNext)) && <Box display='flex' flexDir='row' alignItems='center' justifyContent='space-between' py={0} >
                    <IconButton aria-label="előző" icon={<MdArrowBack />} variant='ghost' isDisabled={!pager.onPrev} onClick={pager.onPrev} />
                    <Box fontSize='sm' color='InactiveCaptionText'>{pager.pageNo}</Box>
                    <IconButton aria-label="előző" icon={<MdArrowForward />} variant='ghost' isDisabled={!pager.onNext} onClick={pager.onNext} />
                </Box>}
            </DeepErrorContainer>
        }
    </>

}





// ============================================================================== LOCAL ==================================





export type TPaged<TItem> = { items: TItem[], nextKeys?: number };

export function usePaginatorLocal<TItem>({ items }: { items: TItem[] }) {

    const [pages, setPages] = useState<TPaged<TItem>[]>([]);
    useEffect(() => {
        const _pages: TPaged<TItem>[] = [];
        const pageSize = 7;
        for (let i = 0; i < items.length; i += pageSize) {
            const pageItems = items.slice(i, i + pageSize);
            _pages.push({
                items: pageItems,
                nextKeys: Math.floor(i / pageSize) + 1,
            });
        }
        if (_pages.length > 0)
            delete _pages[_pages.length - 1].nextKeys;
        setPages(_pages);

    }, [items]);


    const pager = usePaginator({
        deps: [pages],
        parse: (p: TPaged<TItem>) => p,
        loadNext: pix => Promise.resolve(pages[pix || 0]),
    });

    return pager;
}















// ============================================================================== HITS ==================================
// -------------------------- CTX ------------------
export interface IHit {
    affair?: Pick<IAffairPub, 'affairKey' | 'affairKind' | 'deletedAt'>;
    user?: Pick<IUserPub, 'userKey' | 'guard' | 'tilted'>;
    group?: Pick<IGeoGroupPub, 'groupKey'>;
}


export interface IHitCtx {
    hit: IHit;
    isActive: boolean;
    highlight?: string[],
};

export const HitContext = createContext<IHitCtx | null>(null);

export interface HitBoxProps extends BoxProps {
    hit: IHit,
    highlight?: string[],
}


export function HitBox({ children, hit, highlight, ...rest }: HitBoxProps) {
    const vvNav = useVVNav();
    const store = useVVStore(
        useShallow((state) => ({
            itemsList: state.itemsList,
            setSuggestionList: state.setSuggestionList,
            suggestionList: state.suggestionList,
            isMobile: state.isMobile(),
        })),
    )

    const onClick = (hit: IHit) => {
        if (hit.affair)
            affairClick(hit.affair.affairKey);
        if (hit.user)
            userClick(hit.user.userKey);
        if (hit.group)
            groupClick(hit.group.groupKey);
    }

    const affairClick = (affairKey: string) => {
        console.log('click');
        const loc = store.itemsList.find(item => isLocationAffair(item) && item.dbAffair.affairKey === affairKey);
        const alreadySugg = !!loc && store.suggestionList.indexOf(loc) !== -1;
        if (store.isMobile || !loc || alreadySugg || !vvNav.isRoot()) {
            vvNav.toDetail('/affair/' + affairKey);
            store.setSuggestionList([]);
        } else {
            store.setSuggestionList(loc ? [loc] : []);
        }
    }

    const userClick = (userKey: string) => {
        console.log('click');
        vvNav.toDetail('/guard/' + userKey);
    }

    const groupClick = (groupKey: string) => {
        vvNav.to(`/group/region/${groupKey}`);
    }

    const currSuggAffairKey = store.suggestionList[0] && isLocationAffair(store.suggestionList[0]) && store.suggestionList[0].dbAffair.affairKey;
    const isActive =
        (!!hit.affair
            && ((window.location.pathname.indexOf('affair/' + hit.affair.affairKey) !== -1)
                || (hit.affair.affairKey === currSuggAffairKey)
            ))
        ||
        (!!hit.user && (window.location.pathname.indexOf('user/' + hit.user.userKey) !== -1))
        ;



    return <HitContext.Provider value={{ hit, highlight, isActive }} >
        <Box
            display='flex' flexDir='row' alignItems='center' gap={1}
            background='#fff1'
            color='#fffd'

            onClick={() => onClick(hit)}
            fontSize='small' lineHeight='normal'
            px={2} py={1} mb={1}
            cursor='pointer'
            _hover={{ bg: '#fff2', color: '#fff' }}
            {...isActive && hit.affair && {
                bg: AFFAIR_KINDS[hit.affair.affairKind].colorDark,
                _hover: { bg: AFFAIR_KINDS[hit.affair.affairKind].color },
            }}
            {...rest}
        >{children}</Box>
    </HitContext.Provider>
}

export function HitBogyo() {
    const ctx = useContext(HitContext);
    return <>
        {ctx?.hit.affair && <Bogyo kind={ctx.hit.affair.affairKind} mr={1} variant={'light'} />}
        {ctx?.hit.user && <BogyoGuard guardKind={ctx.hit.user?.guard?.kind || '00'} />}
        {ctx?.hit.group && <BogyoGroup />}
    </>
}

export function HitRight(rest: BoxProps) {
    const ctx = useContext(HitContext);
    return <>
        <Box flex={1} {...rest} />
        {!!ctx?.hit.affair?.deletedAt && <Box ml='auto'><Icon as={MdVisibilityOff} fontSize='2xl' color='#fff8' /></Box>}
        {!!ctx?.hit.user?.tilted && <Box ml='auto'><Icon as={FaUserSlash} fontSize='2xl' color='#fff8' /></Box>}
    </>
}

export function HitLineSub({ children, ...rest }: BoxProps) {
    return <Box color='#fff8' {...rest} >{children}</Box>
}

export function HitLineMain({ children, ...rest }: BoxProps) {
    const ctx = useContext(HitContext);
    return <Box fontSize='md' fontWeight='400' {...!!ctx?.hit.affair?.deletedAt && { textDecoration: 'line-through' }} >{children}</Box>
}

export function HL({ text }: { text: string }) {
    const ctx = useContext(HitContext);
    return highlight(text, ctx?.highlight || []);
}

export function HitNotFound({ words }: { words: string[] }) {
    return <HStack color='#0ab7ffba'>
        {words.map(w => <Text as='span' textDecor="line-through">{w}</Text>)}
    </HStack>

}


// ======================================================================== HIGHLIGHT HITs =============================
type HighlightPart = {
    text: string;
    isHighlight: boolean;
};

export function highlight(original: string, keywords: string[]) {
    if (!keywords.length) {
        return <>{original}</>;
    }
    keywords = keywords.map(kw => removeHungarianAccents(kw));

    // Escape regex special chars in keywords
    const escaped = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const regex = new RegExp(`(${escaped.join("|")})`, "gi");

    const parts: HighlightPart[] = [];
    let lastIndex = 0;

    //original = removeHungarianAccents(original.toLowerCase());
    removeHungarianAccents(original.toLowerCase()).replace(regex, (match, _g, offset) => {
        if (offset > lastIndex) {
            parts.push({
                text: original.slice(lastIndex, offset),
                isHighlight: false
            });
        }
        parts.push({
            text: original.slice(offset, offset + match.length),//match,
            isHighlight: true
        });
        lastIndex = offset + match.length;
        return match;
    });

    if (lastIndex < original.length) {
        parts.push({
            text: original.slice(lastIndex),
            isHighlight: false
        });
    }

    return <>
        {parts.map((part, ix) => <Text as='span' key={ix}
            {...part.isHighlight && { bg: '#00000059', color: '#fbec66', display: 'inline-block', px: 0, borderRadius: 'md' }} >
            {part.text}
        </Text>)}
    </>;
}
